import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { normalizeRun } from '../workflow/workflow-run.types';
import { useTriggerWorkflowsMap } from './hooks';

/** One real run, reduced to what matching it against a schedule needs. */
export type ScheduledRunRecord = {
  triggerId: string;
  at: Date;
  status: string;
};

/**
 * Row cap for the window. Generous — a schedule view wants the whole window, and
 * the server caps it again — but bounded, because one busy cron shouldn't decide
 * the payload. When the server reports `truncated`, callers stop treating a
 * missing run as evidence that a schedule didn't fire.
 */
const RUNS_LIMIT = 2000;

/**
 * Actual runs for a set of triggers inside a date window, so a schedule view can
 * say what *happened* at each past occurrence rather than only what was planned.
 *
 * Runs belong to workflows, not triggers, so this resolves the bindings first
 * (batched — one request for every trigger on screen) and then fetches the runs
 * for all of those workflows in a *single* request. `WorkflowRun` carries
 * `triggerId`, so the rows regroup onto the trigger that fired them with no
 * extra joins.
 *
 * The one-request part is load-bearing: fanning out per workflow meant a request
 * per row on a page whose entire job is showing many rows, and it fell over
 * around a couple of dozen triggers.
 *
 * Test runs are dropped: a "Trigger now" click isn't a scheduled occurrence, and
 * counting it as one would make a schedule look honoured when it wasn't.
 */
export function useScheduledRunRecords(
  triggerIds: string[],
  windowStart: Date,
  windowEnd: Date
): {
  recordsByTriggerId: Map<string, ScheduledRunRecord[]>;
  /** False until run history is in — callers must not read absence as "missed". */
  loaded: boolean;
  /** True when the window's runs were clipped by the row cap. */
  truncated: boolean;
  /**
   * The earliest instant run history is known to be complete from.
   *
   * The batch query orders `created_at DESC` and clips to the row cap, so the
   * rows it drops are the *oldest* of the window. Absence of a run is therefore
   * only evidence after this point; before it, a slot may well have fired and
   * simply not been returned. `Number.NEGATIVE_INFINITY` when nothing was
   * clipped, meaning the whole window is covered.
   */
  coverageStartMs: number;
  /** True when run history could not be fetched, so `loaded` will never turn true. */
  failed: boolean;
  /**
   * Triggers with at least one *enabled* binding — the ones that can actually
   * produce a run. A disabled binding still has a workflow row, so a caller that
   * asks "does this trigger have a workflow?" would plot schedules that are
   * switched off and then report every slot as unrecorded.
   */
  runnableTriggerIds: Set<string>;
  /** False until bindings are in; `runnableTriggerIds` is not yet meaningful. */
  bindingsLoaded: boolean;
} {
  const orgId = useWhoami((s) => s.org.id);
  const client = useTenantedClient();
  const { workflowsByTriggerId, isLoading: bindingsLoading } =
    useTriggerWorkflowsMap(triggerIds);

  const workflowIds = useMemo(() => {
    const ids = new Set<string>();
    for (const workflows of workflowsByTriggerId.values()) {
      for (const workflow of workflows) {
        ids.add(workflow.workflowId);
      }
    }
    // Sorted so the query key is stable across binding-map reorderings.
    return [...ids].sort();
  }, [workflowsByTriggerId]);

  const createdAfter = windowStart.toISOString();
  const createdBefore = windowEnd.toISOString();
  const enabled = workflowIds.length > 0;

  const { data, isError } = useQuery({
    queryKey: queryKeys.workflow.runsByWorkflows({
      orgId,
      workflowIds,
      createdAfter,
      createdBefore,
    }),
    enabled,
    queryFn: async (): Promise<{
      records: ScheduledRunRecord[];
      truncated: boolean;
    }> => {
      const res = await client.resource.workflow.runsByWorkflows({
        workflowIds,
        createdAfter,
        createdBefore,
        limit: RUNS_LIMIT,
      });
      if (res.isErr()) {
        // Thrown, not swallowed as an empty result. Returning `{ records: [] }`
        // resolves the query, which flips `loaded` to true and makes every past
        // slot read as "No run recorded" — a network blip would invent a page of
        // missed runs. Failing keeps `data` undefined, so occurrences stay
        // `pending` and claim nothing.
        throw new Error(
          res.error.t === 'application'
            ? res.error.err.message
            : 'Failed to load run history'
        );
      }
      const records: ScheduledRunRecord[] = [];
      for (const item of res.value.items) {
        const run = normalizeRun(item);
        if (run.isTest || run.triggerId === '') {
          continue;
        }
        records.push({
          triggerId: run.triggerId,
          at: run.createdAt,
          status: run.status,
        });
      }
      return { records, truncated: res.value.truncated };
    },
  });

  const runnableTriggerIds = useMemo(() => {
    const ids = new Set<string>();
    for (const [triggerId, workflows] of workflowsByTriggerId) {
      if (workflows.some((workflow) => workflow.bindingEnabled !== false)) {
        ids.add(triggerId);
      }
    }
    return ids;
  }, [workflowsByTriggerId]);

  const recordsByTriggerId = useMemo(() => {
    const map = new Map<string, ScheduledRunRecord[]>();
    for (const record of data?.records ?? []) {
      const list = map.get(record.triggerId);
      if (list !== undefined) {
        list.push(record);
      } else {
        map.set(record.triggerId, [record]);
      }
    }
    for (const list of map.values()) {
      list.sort((a, b) => a.at.getTime() - b.at.getTime());
    }
    return map;
  }, [data]);

  // The clip is global (one `ORDER BY created_at DESC` across every workflow),
  // so the boundary is the oldest run in the whole response, not per trigger.
  const coverageStartMs = useMemo(() => {
    if (data === undefined || !data.truncated) {
      return Number.NEGATIVE_INFINITY;
    }
    let earliest = Number.POSITIVE_INFINITY;
    for (const record of data.records) {
      earliest = Math.min(earliest, record.at.getTime());
    }
    return earliest;
  }, [data]);

  return {
    recordsByTriggerId,
    loaded: enabled ? data !== undefined : !bindingsLoading,
    truncated: data?.truncated ?? false,
    coverageStartMs,
    failed: isError,
    runnableTriggerIds,
    bindingsLoaded: !bindingsLoading,
  };
}
